import { AddressZero } from '@ethersproject/constants';
import CopyPopUp from '../../../../../../../components/CopyPopUp';
import Detail from '../../../../../../../components/Detail';
import { formatAccount, formatPercentages } from '../../../../../../../helpers/formats';
import { SpoolDto } from '../../../../../../../types/spool-local';
import InfoPopUp from '../../../../../../../components/InfoPopUp';
import IncentivizeReward from '../../../../../../../modals/infoPopUp/IncentivizeReward';
import { IncentivizedArrowIcon } from '../../../../../../../assets/icons';
import { getRiskScore, mapRiskToRange } from '../../../../../../../helpers/sdkHelper';
import AdjustedApy from '../../../../../../../modals/infoPopUp/AdjustedApy';
import moment from 'moment';
import ExternalLink from '../../../../../../../components/ExternalLink';
import { config } from '../../../../../../../config/config';
import { SupportedChains } from '../../../../../../../config/constants';
import { useWeb3React } from '@web3-react/core';
import appContext from '../../../../../../../context/app/appContext';
import { useContext } from 'react';

type Props = {
    spoolDetails: SpoolDto;
};

const SpoolCreatorFeesDetails: React.FC<Props> = ({ spoolDetails }) => {
    const { chainId } = useWeb3React();

    const { chainStrategies, chainRiskModels } = useContext(appContext);

    const {
        owner,
        address,
        riskModelAddress,
        performanceFees,
        riskTolerance,
        dateCreated,
        incentivizedRewards,
        baseApy,
        strategies,
        adjustedApy,
    } = spoolDetails;
    return (
        <div className='spool-creator-fees-details'>
            <Detail
                className='owner-address'
                title='Spool Owner Address'
                value={
                    <>
                        <span>{formatAccount(owner || AddressZero)}</span>
                        <CopyPopUp text={owner || AddressZero} />
                    </>
                }
            />
            <Detail
                className='risk-model'
                title='Risk Model'
                value={<div>{chainRiskModels[riskModelAddress]?.name || 'Model'}</div>}
            />
            <Detail
                className='strategies'
                title='Strategies'
                value={strategies.map(({ address: strategyAddress }, index) => (
                    <InfoPopUp
                        className='strategy'
                        triggerMargin={{ x: 0, y: 3 }}
                        key={index}
                        trigger={
                            <span>
                                {chainStrategies[strategyAddress]?.name || 'Strategy'}
                            </span>
                        }
                    >
                        {chainStrategies[strategyAddress]?.detail || 'Strategy Detail'}
                    </InfoPopUp>
                ))}
            />
            <Detail
                className='total-performance-fee'
                title={
                    <>
                        <span>Total Performance Fee</span>
                        <InfoPopUp>
                            <span>
                                Fees are based on the user’s profits earned through the
                                Spool and are distributed on withdrawal.
                            </span>
                        </InfoPopUp>
                    </>
                }
                value={`${
                    Object.values(performanceFees)
                        .reduce((totalFee, fee) => (totalFee += +fee), 0)
                        .toFixed(2) || '0.00'
                }%`}
            />
            <Detail
                title={`Risk Model's Performance Fee`}
                value={`${(+performanceFees.riskProvider).toFixed(2) || '0.00'}%`}
            />
            <Detail
                className='incentivized-rewards'
                title={
                    <>
                        <span>Incentivized Rewards</span>
                        <IncentivizeReward incentivizedRewards={incentivizedRewards} />
                    </>
                }
                value={
                    <>
                        {incentivizedRewards?.length
                            ? incentivizedRewards
                                  .filter(({ isRemoved }) => !isRemoved)
                                  .map(({ symbol, apy, id }) => (
                                      <div key={id} className='reward'>
                                          <div className='token'>
                                              <span>{symbol || 'ASSET'}</span>
                                              <span className='apy'>
                                                  {formatPercentages(
                                                      parseFloat(apy) * 100 || 0
                                                  )}
                                                  % APY
                                              </span>
                                          </div>
                                          <IncentivizedArrowIcon incentivized />
                                      </div>
                                  ))
                            : 'N/A'}
                    </>
                }
            />
            <Detail
                className='risk-tolerance'
                title='Risk Appetite'
                value={
                    <>
                        <span>{getRiskScore(+riskTolerance)}</span> (
                        {mapRiskToRange(+riskTolerance) || '0'})
                    </>
                }
            />
            <Detail
                title='Stablecoin APY'
                value={`${formatPercentages(parseFloat(baseApy) * 100 || 0)}%`}
            />
            <Detail
                className='adjusted-apy-after-fees'
                title={
                    <>
                        <span>
                            Adjusted APY After Performance Fees and Incentivized Rewards
                        </span>
                        <AdjustedApy
                            adjustedApy={adjustedApy}
                            baseApy={baseApy}
                            incentivizedRewards={incentivizedRewards}
                            performanceFees={performanceFees}
                        />
                    </>
                }
                value={`${formatPercentages(parseFloat(adjustedApy) * 100 || 0)}%`}
            />
            <Detail
                title='Date Created:'
                value={moment.unix(+dateCreated).format('YYYY MMMM DD')}
            />
            {/* <div className='url'>
                <p>
                    Etherscan URL :{' '}
                    <ExternalLink to={`${config.urls.etherscan}/address/${address}`}>
                        {config.urls.etherscan}/address/{address}
                    </ExternalLink>
                </p>
                <CopyPopUp text={`${config.urls.etherscan}/address/${address}`} />
            </div> */}
            <Detail
                className='url'
                title={`${
                    chainId !== SupportedChains.ARBITRUM_ONE ? 'Etherscan' : 'Arbiscan'
                } URL`}
                value={
                    <>
                        <ExternalLink
                            to={`${
                                chainId !== SupportedChains.ARBITRUM_ONE
                                    ? config.urls.etherscan
                                    : config.urls.arbiscan
                            }/address/${address}`}
                        >
                            {chainId !== SupportedChains.ARBITRUM_ONE
                                ? config.urls.etherscan
                                : config.urls.arbiscan}
                            /address/{address}
                        </ExternalLink>
                        <CopyPopUp
                            text={`${
                                chainId !== SupportedChains.ARBITRUM_ONE
                                    ? config.urls.etherscan
                                    : config.urls.arbiscan
                            }/address/${address}`}
                        />
                    </>
                }
            />
        </div>
    );
};

export default SpoolCreatorFeesDetails;
